//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import _ from 'underscore';

import { DEFAULT_EMPTY_VALUE } from '@constants/DefaultEmptyValues';
import { MonetaryValue } from '@models/MonetaryValue';
import { isDefined, isNotNull } from '@utils/filterPredicates';

// Options for handling the +/- prefix on readable numbers
export enum ReadableNumberSignOption {
  NONE = 'none', // Display no sign prefix
  NEGATIVE = 'negative', // Only display a sign prefix for negative numbers
  BOTH = 'both', // Always display a sign prefix
}

export interface IReadableNumberOptions {
  sign?: ReadableNumberSignOption;
  startAbbreviatingAt?: number;
  rateDecimals?: number;
  rateIncludePercentSuffix?: boolean;
}

const defaultOptions: IReadableNumberOptions = {
  sign: ReadableNumberSignOption.NEGATIVE,
  startAbbreviatingAt: 0,
  rateDecimals: 2,
  rateIncludePercentSuffix: true,
};

function applyOverrideOptions(
  overrides?: IReadableNumberOptions,
): IReadableNumberOptions {
  if (overrides) {
    return {
      ...defaultOptions,
      ...(overrides || {}),
    };
  } else {
    return defaultOptions;
  }
}

export function calculateSign(
  val: number,
  options: IReadableNumberOptions,
): string {
  if (options.sign === ReadableNumberSignOption.BOTH) {
    return val < 0 ? '-' : '+';
  } else if (options.sign === ReadableNumberSignOption.NEGATIVE) {
    return val < 0 ? '-' : '';
  } else {
    return '';
  }
}

export const UNKNOWN_CURRENCY = 'UNKNOWN';

// These are the currency symbols from Google Analytics. Automated reporting also has a similar list.
export function getRevenueSymbolFromFormat(
  currencyFormat?: string,
  shouldUseDollarSignAsDefault = true,
): string {
  let symbol;
  switch (currencyFormat) {
    case 'AED':
      symbol = 'AED';
      break;
    case 'ARS':
      symbol = 'ARS';
      break;
    case 'AUD':
      symbol = 'A$';
      break;
    case 'BGN':
      symbol = 'BGN';
      break;
    case 'BOB':
      symbol = 'BOB';
      break;
    case 'BRL':
      symbol = 'R$';
      break;
    case 'CAD':
      symbol = 'CA$';
      break;
    case 'CHF':
      symbol = 'CHF';
      break;
    case 'CLP':
      symbol = 'CLP';
      break;
    case 'CNY':
      symbol = 'CN¥';
      break;
    case 'COP':
      symbol = 'COP';
      break;
    case 'CZK':
      symbol = 'CZK';
      break;
    case 'DKK':
      symbol = 'DKK';
      break;
    case 'EGP':
      symbol = 'EGP';
      break;
    case 'EUR':
      symbol = '€';
      break;
    case 'FRF':
      symbol = 'FRF';
      break;
    case 'GBP':
      symbol = '£';
      break;
    case 'HKD':
      symbol = 'HK$';
      break;
    case 'HRK':
      symbol = 'HRK';
      break;
    case 'HUF':
      symbol = 'HUF';
      break;
    case 'IDR':
      symbol = 'IDR';
      break;
    case 'ILS':
      symbol = '₪';
      break;
    case 'INR':
      symbol = '₹';
      break;
    case 'JPY':
      symbol = '¥';
      break;
    case 'KRW':
      symbol = '₩';
      break;
    case 'LTL':
      symbol = 'LTL';
      break;
    case 'MAD':
      symbol = 'MAD';
      break;
    case 'MXN':
      symbol = 'MX$';
      break;
    case 'MYR':
      symbol = 'MYR';
      break;
    case 'NOK':
      symbol = 'NOK';
      break;
    case 'NZD':
      symbol = 'NZ$';
      break;
    case 'PEN':
      symbol = 'PEN';
      break;
    case 'PHP':
      symbol = 'PHP';
      break;
    case 'PKR':
      symbol = 'PKR';
      break;
    case 'PLN':
      symbol = 'PLN';
      break;
    case 'RON':
      symbol = 'RON';
      break;
    case 'RUB':
      symbol = 'RUB';
      break;
    case 'RSD':
      symbol = 'RSD';
      break;
    case 'SAR':
      symbol = 'SAR';
      break;
    case 'SEK':
      symbol = 'SEK';
      break;
    case 'SGD':
      symbol = 'SGD';
      break;
    case 'THB':
      symbol = 'THB';
      break;
    case 'TRY':
      symbol = 'TRY';
      break;
    case 'TWD':
      symbol = 'TW$';
      break;
    case 'UAH':
      symbol = 'UAH';
      break;
    case 'USD':
      symbol = '$';
      break;
    case 'VEF':
      symbol = 'VEF';
      break;
    case 'VND':
      symbol = '₫';
      break;
    case 'ZAR':
      symbol = 'R';
      break;
    case UNKNOWN_CURRENCY:
      symbol = '';
      break;
    default:
      symbol = shouldUseDollarSignAsDefault ? '$' : currencyFormat ?? '';
      break;
  }
  return symbol;
}

// Regex for grouping a number into three digit segments
const digitGroupingRegex = /\B(?=(\d{3})+(?!\d))/g;

export function readableNumber(
  val: number,
  options?: IReadableNumberOptions,
): string {
  const minAbbreviatingNumber = options?.startAbbreviatingAt;

  if (
    isDefined(minAbbreviatingNumber) &&
    Math.abs(val) < minAbbreviatingNumber
  ) {
    return commaSeparatedNumber(val, options);
  }

  const finalOptions = applyOverrideOptions(options);

  const units = ['', 'K', 'M', 'B', 'T'];
  const kilo = 1000;
  const decimals = 1; // decimal places to render
  const sign = calculateSign(val, finalOptions);

  let fVal = Math.abs(val); // Normalize number for formatting
  let unit = units.length - 1;
  let intPart;
  let decimalString;

  if (fVal < 10000) {
    // Display all digits if we're in the thousands
    return sign + fVal.toFixed(0).replace(digitGroupingRegex, ',');
  } else if (fVal < 100000) {
    // For numbers between 10K and 100K round to three sig figs
    fVal = Math.round(fVal / 100) * 100;
  } else if (fVal < 1000000) {
    // For numbers between 100K and 1M round to three sig figs
    fVal = Math.round(fVal / 1000) * 1000;
  } else if (fVal < 10000000) {
    // For numbers between 1M and 10M round to two sig figs
    fVal = Math.round(fVal / 100000) * 100000;
  } else if (fVal < 100000000) {
    // For numbers between 10M and 100M round round to three sig figs
    fVal = Math.round(fVal / 100000) * 100000;
  } else if (fVal < 1000000000) {
    // For numbers between 100M and 1B round round to three sig figs
    fVal = Math.round(fVal / 10000) * 10000;
  } else if (fVal < 10000000000) {
    // For numbers between 1B and 10B round round to two sig figs
    fVal = Math.round(fVal / 100000000) * 100000000;
  } else if (fVal < 100000000000) {
    // For numbers between 10B and 100B round round to three sig figs
    fVal = Math.round(fVal / 100000000) * 100000000;
  } else {
    // For numbers between 100B and 1T round round to three sig figs
    fVal = Math.round(fVal / 10000000) * 10000000;
  }

  for (let i = 0; i < units.length; i++) {
    if (fVal < Math.pow(kilo, i + 1)) {
      unit = i;
      break;
    }
  }

  const humanized = fVal / Math.pow(kilo, unit);
  const suffix = units[unit] ? units[unit] : '';

  // Grab the integer part of the number
  intPart = parseInt(humanized.toFixed(decimals), 10).toString();

  // If the int part of our formatted result is 3 digits, make sure we round the humanized result again
  // because we aren't going to display a decimal point to contextualize our initial rounding
  if (intPart.length === 3) {
    intPart = parseInt(Math.round(humanized).toFixed(decimals), 10).toString();
  }

  // Format the decimals, but only if the integer part's length is < 3
  if (intPart.length !== 3) {
    decimalString =
      '.' +
      Math.abs(humanized - +intPart)
        .toFixed(decimals)
        .slice(2);
  } else {
    decimalString = '';
  }

  return `${sign}${intPart.replace(
    digitGroupingRegex,
    ',',
  )}${decimalString}${suffix}`;
}

export function readableRate(
  val: number,
  options?: IReadableNumberOptions,
): string {
  const finalOptions = applyOverrideOptions(options);

  const sign = calculateSign(val, finalOptions);
  const fVal = Math.abs(val); // Normalize number for formatting

  const percentSuffix = finalOptions.rateIncludePercentSuffix ? '%' : '';

  return `${sign}${fVal.toFixed(finalOptions.rateDecimals)}${percentSuffix}`;
}

export function optionalReadableRate(
  val?: number,
  options?: IReadableNumberOptions,
): string {
  if (!isDefined(val)) {
    return '--';
  }
  return readableRate(val, options);
}

export function commaSeparatedNumber(
  val: number,
  options?: IReadableNumberOptions,
): string {
  const finalOptions = applyOverrideOptions(options);

  const sign = calculateSign(val, finalOptions);
  const fVal = Math.abs(val); // Normalize number for formatting

  return `${sign}${fVal.toFixed(0).replace(digitGroupingRegex, ',')}`;
}

export function optionalCommaSeparatedNumber(
  val?: number,
  options?: IReadableNumberOptions,
): string {
  if (!isDefined(val)) {
    return '--';
  }
  return commaSeparatedNumber(val, options);
}

export function readablePrice(
  val: number | string,
  hideDecimal?: boolean,
  currencyFormat?: string,
  useShortScale: boolean = true,
): string {
  if (!_(val).isNumber()) {
    return val.toString();
  }

  let valString = val.toString();
  let valNumber = parseFloat(valString);

  const shouldHideDecimal = !!hideDecimal || valNumber >= 10000;
  if (shouldHideDecimal) {
    valNumber = Math.round(valNumber);
    valString = valNumber.toString();
  }

  const valStringParts = valString.split('.');

  const currencySymbol = getRevenueSymbolFromFormat(currencyFormat);

  const wholePart = renderToReadableNumber(
    Math.abs(Number(valStringParts[0]) || 0),
    useShortScale,
  );

  const sign = valNumber < 0 ? '-' : '';

  if (!shouldHideDecimal) {
    let decimalPart = (valStringParts[1] || '00').substring(0, 3);

    if (decimalPart.length === 1) {
      decimalPart += '0';
    } else if (decimalPart.length > 2) {
      decimalPart = Math.floor(
        parseFloat(`0.${decimalPart}`) * 100.0,
      ).toString();
      if (decimalPart.length === 1) {
        decimalPart += '0';
      }
    } else if (decimalPart.length < 2) {
      decimalPart = `0${decimalPart}`;
    }

    return `${sign}${currencySymbol}${wholePart}.${decimalPart}`;
  }

  return `${sign}${currencySymbol}${wholePart}`;
}

/*
 * copied from js / curalate.js for testing purposes
 * modified to make shortScale optional
 */
function renderToReadableNumber(
  val: number | string,
  useShortScale: boolean,
): string {
  const units = ['', 'K', 'M', 'B', 'T'];
  const kilo = 1000;
  const decimals = 1;

  const num = typeof val === 'number' ? val : parseFloat(val);

  if (isNaN(num)) {
    return val.toString();
  }

  const sign = num < 0 ? '-' : '';
  let fVal = Math.abs(num);

  // If short scale is not used, return the number with commas and decimals
  if (!useShortScale) {
    return sign + fVal.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  if (fVal < 10_000) {
    return sign + fVal.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  } else if (fVal < 100_000) {
    fVal = Math.round(fVal / 100) * 100;
  } else if (fVal < 1_000_000) {
    fVal = Math.round(fVal / 1_000) * 1_000;
  } else if (fVal < 1_000_000_000) {
    fVal = Math.round(fVal / 100_000) * 100_000;
  } else {
    fVal = Math.round(fVal / 1_000_000_000) * 1_000_000_000;
  }

  let unit = units.length - 1;
  for (let i = 0; i < units.length; i++) {
    if (fVal < Math.pow(kilo, i + 1)) {
      unit = i;
      break;
    }
  }

  const humanized = fVal / Math.pow(kilo, unit);
  const suffix = units[unit] ?? '';

  let intPart = parseInt(humanized.toFixed(decimals), 10).toString();
  let decimalString = '';

  if (intPart.length === 3) {
    intPart = Math.round(humanized).toFixed(decimals).toString();
    intPart = parseInt(intPart, 10).toString();
  }

  if (intPart.length !== 3) {
    decimalString =
      '.' +
      Math.abs(humanized - parseInt(intPart))
        .toFixed(decimals)
        .slice(2);
  }

  return (
    sign +
    intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',') +
    decimalString +
    suffix
  );
}

/**
 * Converts a numeric or string monetary value into a readable currency format with a currency symbol.
 * Handles cases like thousands separators, decimal rounding, and ensures correct formatting even for string inputs.
 * This function doesn't use short-scale such as 10k, 1.1M.
 * No rounding
 * Also displays 2 decimal points
 *
 * @param {number | string} val - The monetary value to format.
 * @param {string} [currencyFormat] - Optional format used to determine the appropriate currency symbol.
 * @returns {string} A formatted string with currency symbol and two decimal places (e.g. "$1,234.56").
 */
export function readableMonetaryValue(
  val: number | string,
  currencyFormat?: string,
): string {
  const valString = val.toString();
  const valNumber = parseFloat(valString);
  if (isNaN(valNumber) || !/^-?\d+(\.\d+)?$/.test(valString)) {
    return valString;
  }

  const valStringParts = valString.split('.');

  const currencySymbol = getRevenueSymbolFromFormat(currencyFormat, false);

  const wholePart = renderToReadableNumber(
    Math.abs(Number(valStringParts[0]) || 0),
    false,
  );

  const sign = valNumber < 0 ? '-' : '';

  let decimalPart = (valStringParts[1] ?? '00').substring(0, 3);

  if (decimalPart.length === 1) {
    decimalPart += '0';
  } else if (decimalPart.length > 2) {
    decimalPart = Math.floor(parseFloat(`0.${decimalPart}`) * 100.0).toString();
    if (decimalPart.length === 1) {
      decimalPart += '0';
    }
  } else if (decimalPart.length < 2) {
    decimalPart = `0${decimalPart}`;
  }

  return `${sign}${currencySymbol}${wholePart}.${decimalPart}`;
}

export function readableTimeFromMs(val: number | undefined) {
  if (!_.isNumber(val)) {
    return '--';
  }

  const totalTimeSeconds = val / 1000;
  const minutes = Math.floor(totalTimeSeconds / 60);
  const seconds = Math.floor(totalTimeSeconds % 60);
  const displayMinutes = minutes > 0 ? `${minutes}m, ` : '';
  return `${displayMinutes}${seconds}s`;
}

export function readableDurationFromSeconds(durationSeconds: number) {
  const hours = Math.floor(durationSeconds / 3600);
  const minutes = Math.floor((durationSeconds % 3600) / 60);
  const seconds = Math.round(durationSeconds % 60);

  const displayHours = String(hours).padStart(2, '0');
  const displayMinutes = String(minutes).padStart(2, '0');
  const displaySeconds = String(seconds).padStart(2, '0');

  return `${hours > 0 ? `${displayHours}:` : ''}${displayMinutes}:${displaySeconds}`;
}

export function optionalReadableNumber(
  val?: number,
  options?: IReadableNumberOptions,
): string {
  if (!isDefined(val) || !isNotNull(val)) {
    return '--';
  }
  return readableNumber(val, options);
}

export function optionalReadablePrice(
  val?: number | string,
  hideDecimal?: boolean,
  currencyFormat?: string,
  useShortScale: boolean = true,
): string {
  if (!isDefined(val) || !isNotNull(val)) {
    return '--';
  }
  return readablePrice(val, hideDecimal, currencyFormat, useShortScale);
}

/**
 * Converts a numeric or string monetary value into a human-readable format with a currency symbol.
 * If the value is `undefined or `null` returns a placeholder (`"--"`).
 * This function doesn't use short-scale such as 10k, 1.1M.
 * No rounding
 * Also displays 2 decimal points
 *
 * @param {number | string} [val] - The monetary value to format. Can be a number or string.
 * @param {string} [currencyFormat] - Optional currency format string used to determine the symbol.
 * @returns {string} A formatted monetary string or `"--"` if the value is not defined or null.
 */
export function optionalReadableMonetaryValue(
  val?: number | string,
  currencyFormat?: string,
): string {
  if (!isDefined(val) || !isNotNull(val)) {
    return DEFAULT_EMPTY_VALUE;
  }
  return readableMonetaryValue(val, currencyFormat);
}

export const optionalMonetaryValue = (
  { currency, value }: MonetaryValue,
  hideDecimal?: boolean,
): string => {
  if (!isDefined(value) || !isDefined(currency)) {
    return DEFAULT_EMPTY_VALUE;
  }

  return readablePrice(value, hideDecimal, currency);
};

/**
 *
 * Multiplies the decimal value with 100 before calling the readableRate function
 *
 * @param  {number} val?
 * @param  {IReadableNumberOptions} options?
 */
export function optionalReadableRateWithRawDecimal(
  val?: number,
  options?: IReadableNumberOptions,
) {
  if (!isDefined(val)) {
    return '--';
  }

  return readableRate(val * 100, options);
}
